import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ViewId } from '../config/navConfig';

// ── Types ─────────────────────────────────────────────────────

export type Theme = 'light' | 'dark';
export type ViewMode = 'card' | 'table';

export interface UserPreferences {
  theme: Theme;
  defaultViewMode: ViewMode;
}

export interface PreferencesContextValue {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  resetPreferences: () => void;
  // Backward-compatible accessors (matches old ThemeContext API)
  isDark: boolean;
  toggleTheme: () => void;
  // Collapsed view state management
  collapsedViews: Set<ViewId>;
  toggleViewCollapsed: (viewId: ViewId) => void;
}

// ── Constants ─────────────────────────────────────────────────

const STORAGE_KEY = 'kdu-preferences';
const LEGACY_THEME_KEY = 'kdu-theme';
export const COLLAPSED_VIEWS_STORAGE_KEY = 'kdu-nav-collapsed-views';
const COLLAPSED_VIEWS_DEBOUNCE_MS = 300;

const VALID_VIEW_IDS: ViewId[] = ['overview', 'weekend', 'planning', 'communications', 'admin', 'settings'];

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'light',
  defaultViewMode: 'card',
};

const VALID_THEMES: Theme[] = ['light', 'dark'];
const VALID_VIEW_MODES: ViewMode[] = ['card', 'table'];

// ── Helpers ───────────────────────────────────────────────────

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__ls_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function validateTheme(value: unknown): Theme {
  if (VALID_THEMES.includes(value as Theme)) return value as Theme;
  return DEFAULT_PREFERENCES.theme;
}

function validateViewMode(value: unknown): ViewMode {
  if (VALID_VIEW_MODES.includes(value as ViewMode)) return value as ViewMode;
  return DEFAULT_PREFERENCES.defaultViewMode;
}

function readPreferencesFromStorage(storageAvailable: boolean): UserPreferences {
  if (!storageAvailable) return { ...DEFAULT_PREFERENCES };

  // Legacy migration: if old key exists and new key doesn't, migrate
  const newStored = localStorage.getItem(STORAGE_KEY);
  const legacyTheme = localStorage.getItem(LEGACY_THEME_KEY);

  if (!newStored && legacyTheme) {
    const migrated: UserPreferences = {
      ...DEFAULT_PREFERENCES,
      theme: validateTheme(legacyTheme),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      localStorage.removeItem(LEGACY_THEME_KEY);
    } catch {
      // If write fails, still return migrated value for in-memory use
    }
    return migrated;
  }

  if (!newStored) return { ...DEFAULT_PREFERENCES };

  try {
    const parsed = JSON.parse(newStored);
    if (typeof parsed !== 'object' || parsed === null) {
      return { ...DEFAULT_PREFERENCES };
    }
    // Extract only known keys and validate values
    return {
      theme: validateTheme(parsed.theme),
      defaultViewMode: validateViewMode(parsed.defaultViewMode),
    };
  } catch {
    // Malformed JSON → defaults
    return { ...DEFAULT_PREFERENCES };
  }
}

function persistPreferences(prefs: UserPreferences, storageAvailable: boolean): void {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Silently fail (e.g., quota exceeded in private browsing)
  }
}

// ── Collapsed Views Helpers ───────────────────────────────────

export function readCollapsedViewsFromStorage(storageAvailable: boolean): Set<ViewId> {
  if (!storageAvailable) return new Set();

  const stored = localStorage.getItem(COLLAPSED_VIEWS_STORAGE_KEY);
  if (!stored) return new Set();

  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set();

    // Only keep valid ViewId values
    const validIds = new Set<string>(VALID_VIEW_IDS);
    const result = new Set<ViewId>();
    for (const item of parsed) {
      if (typeof item === 'string' && validIds.has(item)) {
        result.add(item as ViewId);
      }
    }
    return result;
  } catch {
    // Malformed JSON → default (all expanded)
    return new Set();
  }
}

export function persistCollapsedViews(collapsed: Set<ViewId>, storageAvailable: boolean): void {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(COLLAPSED_VIEWS_STORAGE_KEY, JSON.stringify([...collapsed]));
  } catch {
    // Silently fail
  }
}

// ── Context ───────────────────────────────────────────────────

const PreferencesContext = createContext<PreferencesContextValue>({
  preferences: DEFAULT_PREFERENCES,
  updatePreference: () => {},
  resetPreferences: () => {},
  isDark: false,
  toggleTheme: () => {},
  collapsedViews: new Set(),
  toggleViewCollapsed: () => {},
});

// ── Provider ──────────────────────────────────────────────────

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [storageAvailable] = useState(() => isLocalStorageAvailable());
  const [preferences, setPreferences] = useState<UserPreferences>(() =>
    readPreferencesFromStorage(storageAvailable)
  );
  const [collapsedViews, setCollapsedViews] = useState<Set<ViewId>>(() =>
    readCollapsedViewsFromStorage(storageAvailable)
  );

  // Debounce timer ref for collapsed views persistence
  const collapsedDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apply data-theme attribute whenever theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', preferences.theme);
  }, [preferences.theme]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (collapsedDebounceRef.current) {
        clearTimeout(collapsedDebounceRef.current);
      }
    };
  }, []);

  const updatePreference = useCallback(<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    setPreferences((prev) => {
      // Validate the incoming value
      let validatedValue = value;
      if (key === 'theme') {
        validatedValue = validateTheme(value) as UserPreferences[K];
      } else if (key === 'defaultViewMode') {
        validatedValue = validateViewMode(value) as UserPreferences[K];
      }
      const next = { ...prev, [key]: validatedValue };
      persistPreferences(next, storageAvailable);
      return next;
    });
  }, [storageAvailable]);

  const resetPreferences = useCallback(() => {
    setPreferences({ ...DEFAULT_PREFERENCES });
    if (storageAvailable) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Silently fail
      }
    }
  }, [storageAvailable]);

  const toggleTheme = useCallback(() => {
    setPreferences((prev) => {
      const next = { ...prev, theme: (prev.theme === 'light' ? 'dark' : 'light') as Theme };
      persistPreferences(next, storageAvailable);
      return next;
    });
  }, [storageAvailable]);

  const toggleViewCollapsed = useCallback((viewId: ViewId) => {
    setCollapsedViews((prev) => {
      const next = new Set(prev);
      if (next.has(viewId)) {
        next.delete(viewId);
      } else {
        next.add(viewId);
      }

      // Debounce localStorage persistence
      if (collapsedDebounceRef.current) {
        clearTimeout(collapsedDebounceRef.current);
      }
      collapsedDebounceRef.current = setTimeout(() => {
        persistCollapsedViews(next, storageAvailable);
      }, COLLAPSED_VIEWS_DEBOUNCE_MS);

      return next;
    });
  }, [storageAvailable]);

  const value = useMemo<PreferencesContextValue>(() => ({
    preferences,
    updatePreference,
    resetPreferences,
    isDark: preferences.theme === 'dark',
    toggleTheme,
    collapsedViews,
    toggleViewCollapsed,
  }), [preferences, updatePreference, resetPreferences, toggleTheme, collapsedViews, toggleViewCollapsed]);

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────

export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext);
}
